import axios from 'axios';
import MainURL from '../MainURL';

const API_BASE = `${MainURL}/admintodos`;

export type TodoStatus = 'todo' | 'in_progress' | 'done';
export type TodoPriority = 'high' | 'medium' | 'low';

export type AdminEmployee = {
  id: number;
  name: string;
  department: string | null;
  position: string | null;
};

export type MainTask = {
  id: number;
  title: string;
  description: string | null;
  sort_order: number;
  created_by: number;
};

export type TaskItem = {
  id: number;
  main_task_id: number;
  assignee_id: number;
  created_by?: number;
  title: string;
  description: string | null;
  status: TodoStatus;
  priority: TodoPriority;
  start_date: string | null;
  due_date: string | null;
  assignee_name: string;
  assignee_position: string | null;
  assignee_department: string | null;
};

type ApiListResponse<T> = { ok: boolean; data?: T };
type ApiItemResponse<T> = { ok: boolean; data?: T };
type ApiError = { ok: false; error?: { message?: string } };

function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiError | undefined;
    return data?.error?.message || fallback;
  }
  return fallback;
}

function parseListData<T>(res: { data?: unknown }): T[] {
  const body = res.data as ApiListResponse<T> | undefined;
  if (!body?.ok || !Array.isArray(body.data)) throw new Error('목록 응답이 올바르지 않습니다.');
  return body.data;
}

// ─── Employees ───

export async function fetchEmployees(requesterId: number): Promise<AdminEmployee[]> {
  const res = await axios.get(`${API_BASE}/employees`, { params: { requesterId } });
  return parseListData<AdminEmployee>(res);
}

// ─── Main Tasks (메인업무) ───

export async function fetchMainTasks(requesterId: number): Promise<MainTask[]> {
  const res = await axios.get(`${API_BASE}/main-tasks`, { params: { requesterId } });
  return parseListData<MainTask>(res);
}

export async function createMainTask(
  requesterId: number,
  payload: { title: string; description?: string; sort_order?: number }
): Promise<MainTask> {
  try {
    const res = await axios.post<ApiItemResponse<MainTask>>(`${API_BASE}/main-tasks`, { requesterId, ...payload });
    if (!res.data?.data) throw new Error('생성 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '메인업무 등록에 실패했습니다.'));
  }
}

export async function updateMainTask(
  requesterId: number,
  id: number,
  payload: Partial<{ title: string; description: string | null; sort_order: number }>
): Promise<MainTask> {
  try {
    const res = await axios.patch<ApiItemResponse<MainTask>>(`${API_BASE}/main-tasks/${id}`, { requesterId, ...payload });
    if (!res.data?.data) throw new Error('수정 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '메인업무 수정에 실패했습니다.'));
  }
}

export async function deleteMainTask(requesterId: number, id: number): Promise<void> {
  try {
    await axios.delete(`${API_BASE}/main-tasks/${id}`, { params: { requesterId } });
  } catch (err) {
    throw new Error(getErrorMessage(err, '메인업무 삭제에 실패했습니다.'));
  }
}

// ─── Task Items (작업) ───

export async function fetchTaskItems(requesterId: number): Promise<TaskItem[]> {
  const res = await axios.get(`${API_BASE}/task-items`, { params: { requesterId } });
  return parseListData<TaskItem>(res);
}

export type CreateTaskItemPayload = {
  main_task_id: number;
  assignee_id: number;
  title: string;
  description?: string;
  priority: TodoPriority;
  status?: TodoStatus;
  start_date?: string | null;
  due_date?: string | null;
};

export async function createTaskItem(
  requesterId: number,
  payload: CreateTaskItemPayload
): Promise<TaskItem> {
  try {
    const res = await axios.post<ApiItemResponse<TaskItem>>(`${API_BASE}/task-items`, { requesterId, ...payload });
    if (!res.data?.data) throw new Error('생성 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '작업 등록에 실패했습니다.'));
  }
}

export async function updateTaskItem(
  requesterId: number,
  id: number,
  payload: Partial<{
    title: string;
    description: string | null;
    status: TodoStatus;
    priority: TodoPriority;
    start_date: string | null;
    due_date: string | null;
    main_task_id: number;
    assignee_id: number;
  }>
): Promise<TaskItem> {
  try {
    const res = await axios.patch<ApiItemResponse<TaskItem>>(`${API_BASE}/task-items/${id}`, { requesterId, ...payload });
    if (!res.data?.data) throw new Error('수정 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '작업 수정에 실패했습니다.'));
  }
}

export async function deleteTaskItem(requesterId: number, id: number): Promise<void> {
  try {
    await axios.delete(`${API_BASE}/task-items/${id}`, { params: { requesterId } });
  } catch (err) {
    throw new Error(getErrorMessage(err, '작업 삭제에 실패했습니다.'));
  }
}

// ─── 공지사항 목록 (전체 공유) ───

export type NoticeItem = {
  id: number;
  content: string;
  notice_date: string | null;
  created_by: number;
  created_by_name: string | null;
  updated_by: number | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
};

export async function fetchNotices(requesterId: number): Promise<NoticeItem[]> {
  const res = await axios.get(`${API_BASE}/notices`, { params: { requesterId } });
  return parseListData<NoticeItem>(res);
}

export async function createNotice(
  requesterId: number,
  payload: { content: string; notice_date?: string | null }
): Promise<NoticeItem> {
  try {
    const res = await axios.post<ApiItemResponse<NoticeItem>>(`${API_BASE}/notices`, { requesterId, ...payload });
    if (!res.data?.data) throw new Error('등록 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '공지 등록에 실패했습니다.'));
  }
}

export async function updateNoticeItem(
  requesterId: number,
  id: number,
  payload: { content: string; notice_date?: string | null }
): Promise<NoticeItem> {
  try {
    const res = await axios.put<ApiItemResponse<NoticeItem>>(`${API_BASE}/notices/${id}`, { requesterId, ...payload });
    if (!res.data?.data) throw new Error('수정 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '공지 수정에 실패했습니다.'));
  }
}

export async function deleteNoticeItem(requesterId: number, id: number): Promise<void> {
  try {
    await axios.delete(`${API_BASE}/notices/${id}`, { params: { requesterId } });
  } catch (err) {
    throw new Error(getErrorMessage(err, '공지 삭제에 실패했습니다.'));
  }
}

// ─── 개인별 메모 (공지사항처럼 목록형, 로그인한 본인 것만 조회/작성/수정/삭제 가능) ───

export type PersonalMemoItem = {
  id: number;
  content: string;
  created_at: string;
  updated_at: string;
};

export async function fetchPersonalMemos(requesterId: number): Promise<PersonalMemoItem[]> {
  const res = await axios.get(`${API_BASE}/personal-memos`, { params: { requesterId } });
  return parseListData<PersonalMemoItem>(res);
}

export async function createPersonalMemo(requesterId: number, content: string): Promise<PersonalMemoItem> {
  try {
    const res = await axios.post<ApiItemResponse<PersonalMemoItem>>(`${API_BASE}/personal-memos`, { requesterId, content });
    if (!res.data?.data) throw new Error('메모 등록 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '메모 등록에 실패했습니다.'));
  }
}

export async function updatePersonalMemo(requesterId: number, id: number, content: string): Promise<PersonalMemoItem> {
  try {
    const res = await axios.put<ApiItemResponse<PersonalMemoItem>>(`${API_BASE}/personal-memos/${id}`, { requesterId, content });
    if (!res.data?.data) throw new Error('메모 수정 응답이 올바르지 않습니다.');
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '메모 수정에 실패했습니다.'));
  }
}

export async function deletePersonalMemo(requesterId: number, id: number): Promise<void> {
  try {
    await axios.delete(`${API_BASE}/personal-memos/${id}`, { params: { requesterId } });
  } catch (err) {
    throw new Error(getErrorMessage(err, '메모 삭제에 실패했습니다.'));
  }
}
